import type { Protocol, SamlMapping } from "./constants";
import type { SyncMode } from "./SyncModeFields";

export type MetadataSource = "metadata" | "manual";

type Guide = {
  create: string;
  register: string;
  connect: string;
  groups: string;
  scim?: string;
};

type OidcSetup = {
  guide: Guide;
  issuerPlaceholder: string;
  redirectUriLabel: string;
};

type SamlSetup = {
  guide: Guide;
  metadataLabel: string;
  metadataPlaceholder: string;
  acsUrlLabel: string;
  entityIdLabel: string;
  mapping: SamlMapping;
};

export type IdentityProvider = {
  id: string;
  name: string;
  shortName: string;
  logo: string | null;
  issuerHost: RegExp | null;
  scimUrlTerm: string;
  providerId: string | null;
  syncModes: SyncMode[];
  protocols: { oidc?: OidcSetup; saml?: SamlSetup };
  defaultProtocol: Protocol;
};

const ALL_SYNC_MODES: SyncMode[] = [
  "off",
  "jit",
  "jit_users_and_groups",
  "scim",
];

const SIMPLE_MAPPING: SamlMapping = {
  email: "email",
  firstName: "firstName",
  lastName: "lastName",
  image: "",
};

const ENTRA_CLAIMS = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims";

const OKTA_SCIM =
  "On the app’s **General** tab, edit **App Settings** and turn on **SCIM provisioning**. Then on the **Provisioning** tab, enter the SCIM base URL above as the **SCIM connector base URL** and choose **HTTP Header** authentication. You’ll get the token to paste there after you add the provider.";

const ENTRA_SCIM =
  "In **Enterprise applications**, open the app’s **Provisioning** page and set **Provisioning Mode** to *Automatic*. Enter the SCIM base URL above as the **Tenant URL**. You’ll get the **Secret Token** after you add the provider.";

const OTHER: IdentityProvider = {
  id: "other",
  name: "Other provider",
  shortName: "your identity provider",
  logo: null,
  issuerHost: null,
  scimUrlTerm: "SCIM endpoint",
  providerId: null,
  syncModes: ALL_SYNC_MODES,
  defaultProtocol: "oidc",
  protocols: {
    oidc: {
      issuerPlaceholder: "https://idp.example.com",
      redirectUriLabel: "Redirect URI",
      guide: {
        create: "Create an OIDC web application in your identity provider.",
        register: "Register the redirect URI below.",
        connect:
          "Copy the application’s issuer URL, client ID, and client secret.",
        groups:
          "Configure your provider to send the user’s group names in a claim called `groups`.",
        scim: "Point your provider’s SCIM client at the base URL above. You’ll get its bearer token after you add the provider.",
      },
    },
    saml: {
      metadataLabel: "IdP metadata",
      metadataPlaceholder: "Paste a metadata URL or XML, or drop a .xml file",
      acsUrlLabel: "ACS URL",
      entityIdLabel: "SP entity ID",
      mapping: {
        email: "email",
        firstName: "givenName",
        lastName: "surname",
        image: "",
      },
      guide: {
        create: "Create a SAML application in your identity provider.",
        register: "Enter the ACS URL and entity ID below.",
        connect:
          "Import your provider’s metadata, or enter its entity ID, sign-on URL, and signing certificate.",
        groups:
          "Configure your provider to send the user’s group names in an attribute called `groups`.",
        scim: "Point your provider’s SCIM client at the base URL above. You’ll get its bearer token after you add the provider.",
      },
    },
  },
};

export const identityProviders: IdentityProvider[] = [
  {
    id: "okta",
    name: "Okta",
    shortName: "Okta",
    logo: "okta",
    issuerHost: /(^|\.)okta(preview|-emea)?\.com$/,
    scimUrlTerm: "SCIM connector base URL",
    providerId: "okta",
    syncModes: ALL_SYNC_MODES,
    defaultProtocol: "oidc",
    protocols: {
      oidc: {
        issuerPlaceholder: "https://your-org.okta.com",
        redirectUriLabel: "Sign-in redirect URI",
        guide: {
          create:
            "In the Okta Admin Console, go to **Applications → Applications**, click **Create App Integration**, and choose **OIDC – OpenID Connect** and **Web Application**.",
          register:
            "Add the sign-in redirect URI below, and under **Assignments** choose who can use 3B.",
          connect:
            "Your issuer URL is your Okta domain. Copy the **Client ID** and **Client secret** from the app’s **General** tab.",
          groups:
            "In Okta, add a groups claim named `groups` to the ID token, with the filter *Matches regex* `.*`.",
          scim: OKTA_SCIM,
        },
      },
      saml: {
        metadataLabel: "Metadata URL",
        metadataPlaceholder:
          "https://your-org.okta.com/app/…/sso/saml/metadata",
        acsUrlLabel: "Single sign-on URL",
        entityIdLabel: "Audience URI (SP Entity ID)",
        mapping: SIMPLE_MAPPING,
        guide: {
          create:
            "In the Okta Admin Console, go to **Applications → Applications**, click **Create App Integration**, and choose **SAML 2.0**.",
          register:
            "Under **SAML Settings**, enter the single sign-on URL and audience URI below. Then add attribute statements `email`, `firstName`, and `lastName` mapped to `user.email`, `user.firstName`, and `user.lastName`.",
          connect: "On the app’s **Sign On** tab, copy the **Metadata URL**.",
          groups:
            "In Okta, add a group attribute statement named `groups` to the app’s **SAML Settings**, with the filter *Matches regex* `.*`.",
          scim: OKTA_SCIM,
        },
      },
    },
  },
  {
    id: "entra",
    name: "Microsoft Entra ID",
    shortName: "Entra",
    logo: "microsoftGraph",
    issuerHost: /^(login\.microsoftonline\.com|sts\.windows\.net)$/,
    scimUrlTerm: "Tenant URL",
    providerId: "entra-id",
    syncModes: ALL_SYNC_MODES,
    defaultProtocol: "saml",
    protocols: {
      saml: {
        metadataLabel: "App Federation Metadata Url",
        metadataPlaceholder:
          "https://login.microsoftonline.com/…/federationmetadata.xml?appid=…",
        acsUrlLabel: "Reply URL (Assertion Consumer Service URL)",
        entityIdLabel: "Identifier (Entity ID)",
        mapping: {
          email: `${ENTRA_CLAIMS}/emailaddress`,
          firstName: `${ENTRA_CLAIMS}/givenname`,
          lastName: `${ENTRA_CLAIMS}/surname`,
          image: "",
        },
        guide: {
          create:
            "In the Microsoft Entra admin center, go to **Enterprise applications → New application → Create your own application** and choose the non-gallery option.",
          register:
            "Under **Single sign-on**, choose **SAML** and enter the identifier and reply URL below.",
          connect:
            "Under **SAML Certificates**, copy the **App Federation Metadata Url**.",
          groups:
            "In Entra, add a group claim for **Groups assigned to the application** that sends group display names, and rename the claim to `groups`.",
          scim: ENTRA_SCIM,
        },
      },
      oidc: {
        issuerPlaceholder: "https://login.microsoftonline.com/<tenant-id>/v2.0",
        redirectUriLabel: "Redirect URI",
        guide: {
          create:
            "In the Microsoft Entra admin center, go to **App registrations** and click **New registration**.",
          register:
            "Under **Redirect URI**, choose **Web** and enter the URI below.",
          connect:
            "Your issuer URL uses the **Directory (tenant) ID** from the app’s **Overview** page, which also shows the **Application (client) ID**. Under **Certificates & secrets**, create a client secret and copy its **Value**.",
          groups:
            "In Entra, add a groups claim for **Groups assigned to the application**. Over OIDC, Entra sends group IDs, so synced groups are named by ID.",
          scim: ENTRA_SCIM,
        },
      },
    },
  },
  {
    id: "google",
    name: "Google Workspace",
    shortName: "Google",
    logo: "googleAdminConsole",
    issuerHost: /^accounts\.google\.com$/,
    scimUrlTerm: "SCIM endpoint",
    providerId: "google-workspace",
    // Google only provisions over SCIM to its own catalog apps.
    syncModes: ["off", "jit", "jit_users_and_groups"],
    defaultProtocol: "saml",
    protocols: {
      saml: {
        metadataLabel: "IdP metadata",
        metadataPlaceholder:
          "Drop the metadata file you downloaded, or paste its XML",
        acsUrlLabel: "ACS URL",
        entityIdLabel: "Entity ID",
        mapping: SIMPLE_MAPPING,
        guide: {
          create:
            "In the Google Admin console, go to **Apps → Web and mobile apps → Add app → Add custom SAML app**. On **Google Identity Provider details**, click **Download metadata**.",
          register:
            "On **Service provider details**, enter the ACS URL and entity ID below and set **Name ID format** to *EMAIL*. Map *Primary email*, *First name*, and *Last name* to `email`, `firstName`, and `lastName`, then turn the app on under **User access**.",
          connect: "Upload the metadata file you downloaded.",
          groups:
            "In Google, under **Group membership** in the app’s SAML attribute mapping, pick the groups to send as an attribute named `groups`.",
        },
      },
    },
  },
  OTHER,
];

export function identityProvider(id: string): IdentityProvider {
  return identityProviders.find((p) => p.id === id) ?? OTHER;
}

// Providers don't record the template they were created from, so recognize
// the IdP from its OIDC issuer or SAML entity ID.
export function identityProviderForIssuer(issuer: string): IdentityProvider {
  const host = URL.canParse(issuer) ? new URL(issuer).hostname : null;
  return (
    identityProviders.find((p) => host && p.issuerHost?.test(host)) ?? OTHER
  );
}

export function uniqueProviderId(base: string, taken: string[]): string {
  if (!taken.includes(base)) return base;
  let n = 2;
  while (taken.includes(`${base}-${n}`)) n++;
  return `${base}-${n}`;
}
